import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, PlayCircle, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const SLOTS = ["Weekday Morning", "Weekday Evening", "Weekend Morning", "Weekend Evening"];
const DEFAULT_VIDEO_URL = "https://youtube.com/shorts/YHZfKAWn44E";

function extractYoutubeId(url) {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/|embed\/|v\/))([A-Za-z0-9_-]{11})/);
  return m ? m[1] : "";
}

export default function DemoBookingSection() {
  const [ytUrl, setYtUrl] = useState(DEFAULT_VIDEO_URL);
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [slot, setSlot] = useState(SLOTS[1]);
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/site-config").then(({ data }) => setYtUrl(data?.demo_youtube_url || DEFAULT_VIDEO_URL)).catch(() => {});
  }, []);

  const ytId = extractYoutubeId(ytUrl);
  const isShort = ytUrl.includes("/shorts/");

  const submit = async (e) => {
    e.preventDefault();
    const age = parseInt(childAge, 10);
    if (isNaN(age) || age < 3 || age > 18) { toast.error("Child's age must be between 3 and 18"); return; }
    setBusy(true);
    try {
      await api.post("/demo-requests", {
        child_name: childName,
        child_age: age,
        parent_email: parentEmail,
        whatsapp,
        preferred_slot: slot,
        location,
      });
      setDone(true);
      toast.success("Demo request received! We'll reach out on WhatsApp shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const inputCls = "w-full h-12 px-4 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none text-base font-medium bg-white";

  return (
    <section id="book-demo" className="bg-lemon/40 border-y border-cloud scroll-mt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-watermelon font-bold text-sm mb-4">
            <CalendarCheck className="w-4 h-4" /> Free Demo Class
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ink mb-4 leading-tight">
            See how the abacus can <span className="text-watermelon">transform your child's life.</span>
          </h2>
          <p className="text-lg text-stone">
            Watch a short video on what abacus learning does for children — then book a <strong className="text-ink">free, no-obligation demo class</strong>. We'll call you on WhatsApp to fix a time.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Video */}
          <div className={`rounded-[2.5rem] overflow-hidden card-tactile bg-ink -rotate-1 hover:rotate-0 transition-transform ${isShort ? "w-full max-w-[340px] mx-auto" : ""}`} data-testid="demo-video-card">
            {ytId ? (
              <iframe
                data-testid="demo-youtube-embed"
                src={`https://www.youtube.com/embed/${ytId}?rel=0&modestbranding=1&playsinline=1`}
                className={`w-full block ${isShort ? "aspect-[9/16]" : "aspect-video"}`}
                title="How abacus transforms students' lives"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            ) : (
              <div className="w-full aspect-video flex flex-col items-center justify-center text-white/80 gap-3 p-8 text-center">
                <PlayCircle className="w-16 h-16 text-lemon" />
                <p className="font-heading font-bold text-lg">Demo video coming soon</p>
                <p className="text-sm text-white/60">Our teacher is recording a walkthrough of how abacus learning transforms children.</p>
              </div>
            )}
          </div>

          {/* Booking form */}
          <div className="p-6 sm:p-8 bg-white rounded-[2.5rem] card-tactile">
            {done ? (
              <div data-testid="demo-booking-success" className="text-center py-10">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-leaf/15 text-leaf flex items-center justify-center">
                  <CheckCircle2 className="w-9 h-9" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-ink mb-2">Request received!</h3>
                <p className="text-stone">Thank you! Our teacher will contact you on WhatsApp shortly to schedule {childName.split(" ")[0]}'s free demo class.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="demo-booking-form">
                <h3 className="font-heading text-xl font-bold text-ink mb-1">Book a free demo class</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-ink mb-1" htmlFor="demo-child-name">Child's name</label>
                    <input id="demo-child-name" data-testid="demo-child-name-input" required value={childName} onChange={(e) => setChildName(e.target.value)} className={inputCls} placeholder="e.g., Aarav" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-ink mb-1" htmlFor="demo-child-age">Child's age</label>
                    <input id="demo-child-age" data-testid="demo-child-age-input" required type="number" min="3" max="18" value={childAge} onChange={(e) => setChildAge(e.target.value)} className={inputCls} placeholder="e.g., 8" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-ink mb-1" htmlFor="demo-parent-email">Parent email</label>
                  <input id="demo-parent-email" data-testid="demo-parent-email-input" required type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} className={inputCls} placeholder="you@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-ink mb-1" htmlFor="demo-whatsapp">WhatsApp number</label>
                  <input id="demo-whatsapp" data-testid="demo-whatsapp-input" required value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} className={inputCls} placeholder="+91 98765 43210" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-ink mb-1" htmlFor="demo-slot">Preferred slot</label>
                    <select id="demo-slot" data-testid="demo-slot-select" value={slot} onChange={(e) => setSlot(e.target.value)} className={inputCls}>
                      {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-ink mb-1" htmlFor="demo-location">City / time zone</label>
                    <input id="demo-location" data-testid="demo-location-input" required value={location} onChange={(e) => setLocation(e.target.value)} className={inputCls} placeholder="e.g., Mumbai, IST" />
                  </div>
                </div>
                <button type="submit" data-testid="demo-booking-submit" disabled={busy} className="w-full h-14 rounded-full bg-watermelon text-white font-heading font-bold text-lg btn-tactile hover:bg-watermelon-hover disabled:opacity-70">
                  {busy ? "Booking…" : "Book my free demo class"}
                </button>
                <p className="text-xs text-stone text-center">No payment needed. We'll WhatsApp you to confirm a time.</p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
